import { useState } from "react";
import { useSelector } from "react-redux";
import { message } from "antd";
import { selectLogin } from "../Login/selector";
import { getAuthToken, getAuthUser } from "../../../utils/auth";
import { API_ENDPOINT } from "../../../utils/constants";
import { useSidebarCollapse } from "../../../utils/useSidebarCollapse";
import PageHeader from "../../components/PageHeader";
import Sidebar from "../../components/Sidebar";
import WelcomeBar from "./components/WelcomeBar";
import StatsRow from "./components/StatsRow";
import AppointmentsTable from "./components/AppointmentsTable";
import AppointmentsInjectors from "./injectors";
import type { DateRange } from "./types";
import { InnerLayout, PageContent, PageLayout } from "./style";

export default function AppointmentsPage() {
  const { data } = useSelector(selectLogin);
  const user = data ?? getAuthUser();
  const fullName = user?.full_name ?? "Guest";
  const firstName = fullName.split(" ")[0] ?? "there";

  const [collapsed, toggleCollapsed] = useSidebarCollapse();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>(null);
  const [pdfExporting, setPdfExporting] = useState(false);

  async function handleExportPdf() {
    setPdfExporting(true);
    try {
      const token = getAuthToken();
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const [start, end] = dateRange ?? [null, null];

      // Range picked => filter by date. No range => base export (all appointments).
      const params = new URLSearchParams();
      if (start) params.set("start_date", start.format("YYYY-MM-DD"));
      if (end) params.set("end_date", end.format("YYYY-MM-DD"));

      const query = params.toString();
      const response = await fetch(
        `${API_ENDPOINT}dashboard/appointments/pdf${query ? `?${query}` : ""}`,
        { headers },
      );
      if (response.status === 404) {
        message.info("No appointments to export.");
        return;
      }
      if (!response.ok) {
        throw new Error(`Export failed (${response.status})`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "appointments_report.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      message.success("PDF report downloaded");
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "PDF export failed");
    } finally {
      setPdfExporting(false);
    }
  }

  return (
    <PageLayout hasSider>
      <AppointmentsInjectors />
      <Sidebar
        collapsed={collapsed}
        onToggleCollapse={toggleCollapsed}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />
      <InnerLayout>
        <PageHeader
          routeName="Appointment Dashboard"
          breadcrumbLabel="Appointment Dashboard"
          onMenuClick={() => setMobileNavOpen(true)}
        />
        <PageContent>
          <WelcomeBar
            name={firstName}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            onExportPdf={handleExportPdf}
            pdfExporting={pdfExporting}
          />
          <StatsRow />
          <AppointmentsTable dateRange={dateRange} />
        </PageContent>
      </InnerLayout>
    </PageLayout>
  );
}
